import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CellClickedEvent, ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessService } from '../mess.service';

interface MonthlySummary {
  closed: number;
  pending: number;
  holiday: number;
  notSubmitted: number;
}

interface DailyStatusEntry {
  messBuildingId: number;
  messBuildingName: string;
  daysheetCloseId: number | null;
  daysheetDate: string;
  status: string;
  holiday: boolean;
  holidayReason: string;
  remarks: string | null;
}

export interface KitchenRowView {
  messBuildingId: number;
  messBuildingName: string;
  entityName: string;
  summary: MonthlySummary;
  dailyStatus: DailyStatusEntry[];
  dayStatusMap: Record<number, DailyStatusEntry | null>;
}

@Component({
  selector: 'app-admin-division-report',
  templateUrl: './admin-division-report.component.html',
  styleUrls: ['./admin-division-report.component.scss'],
})
export class AdminDivisionReportComponent implements OnInit {
  month = '';
  year = '';
  divisionName = '';
  monthLabel = '';
  kitchensCount = 0;
  summary: MonthlySummary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
  kitchens: KitchenRowView[] = [];
  visibleDays: number[] = [];
  daysInMonth = 0;
  rowData: Record<string, unknown>[] = [];
  monthlyColDefs: ColDef[] = [];
  hoveredKitchenId: number | null = null;
  kitchenRowHeights: number[] = [];

  readonly kitchenRowHeight = 64;
  kitchenHeaderHeight = 52;

  divisionGridOptions: GridOptions = {
    suppressHorizontalScroll: true,
    domLayout: 'autoHeight',
  };

  divisionDefaultColDef: ColDef = {
    menuTabs: [],
    suppressHeaderMenuButton: true,
    sortable: false,
    filter: false,
    resizable: false,
    flex: 0,
  };

  overlayNoRowsTemplate = '<span>No kitchen data found</span>';

  private gridApi?: GridApi;
  private selectedMonth = moment();
  private readonly monthlyDayMinWidth = 44;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: MessService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    // Landing shell (MessDetails): params from MessService after dashboard → division setView
    if (this.router.url.includes('MessDetails')) {
      const fromService = this.service.getAdminDivisionReportParams();
      if (fromService?.['month'] && fromService?.['year'] && fromService?.['divisionName']) {
        this.applyDivisionParams(fromService);
      }
    }

    // Dedicated /adminDivisionReport route: keep existing queryParams behavior
    this.route.queryParams.subscribe((params) => {
      if (!params?.['month'] || !params?.['year'] || !params?.['divisionName']) {
        return;
      }
      this.applyDivisionParams(params);
    });
  }

  private applyDivisionParams(params: Record<string, any>): void {
    this.month = String(params['month'] ?? '').trim();
    this.year = String(params['year'] ?? '').trim();
    this.divisionName = String(params['divisionName'] ?? '').trim();
    this.syncSelectedMonth();
    this.loadDivisionReport();
  }

  get summaryTotal(): number {
    const s = this.summary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  summaryPercent(value: number, total?: number): string {
    const base = total ?? this.summaryTotal;
    if (!base) {
      return '0%';
    }
    return ((Number(value ?? 0) / base) * 100).toFixed(1) + '%';
  }

  kitchenSummaryTotal(kitchen: KitchenRowView): number {
    const s = kitchen.summary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  kitchenSummaryPercent(kitchen: KitchenRowView, value: number): string {
    return this.summaryPercent(value, this.kitchenSummaryTotal(kitchen));
  }

  goBack(): void {
    if (this.router.url.includes('MessDetails')) {
      this.service.clearAdminDivisionReportParams();
      this.service.setView('adminDashboardReports');
      return;
    }
    this.router.navigate(['/adminDashboardReports']);
  }

  trackKitchen(_index: number, kitchen: KitchenRowView): number {
    return kitchen.messBuildingId;
  }

  getKitchenRowHeight(index: number): number {
    return this.kitchenRowHeights[index] ?? this.kitchenRowHeight;
  }

  onKitchenRowEnter(kitchen: KitchenRowView): void {
    this.hoveredKitchenId = kitchen.messBuildingId;
  }

  onKitchenRowLeave(): void {
    this.hoveredKitchenId = null;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.applyGridData();
  }

  onFirstDataRendered(): void {
    this.syncLayoutHeights();
  }

  onCellClicked(event: CellClickedEvent): void {
    const field = String(event.colDef?.field ?? '');
    if (!field.startsWith('day_')) {
      return;
    }
    const day = Number(field.replace('day_', ''));
    const messBuildingId = Number(event.data?.messBuildingId ?? 0);
    const kitchen = this.kitchens.find((item) => item.messBuildingId === messBuildingId);
    if (!kitchen) {
      return;
    }
    this.onDayCellClick(kitchen, day);
  }

  onDayCellClick(kitchen: KitchenRowView, day: number): void {
    const entry = kitchen.dayStatusMap[day];
    if (!entry) {
      return;
    }
    const statusKey = this.resolveDayStatusKey(entry);

    if (statusKey === 'NOT_SUBMITTED' || statusKey === 'HOLIDAY' || statusKey === 'PENDING') {
      return;
    }

    if (statusKey === 'CLOSED' || statusKey === 'CLOSE') {
      const queryParams: Record<string, string | number> = {
        messBuildingId: kitchen.messBuildingId,
        daysheetDate: entry.daysheetDate,
        messBuildingName: kitchen.messBuildingName,
      };

      // Landing shell: division → inventory via setView (same pattern as mess-do-detail-view)
      if (this.router.url.includes('MessDetails')) {
        this.service.setAdminMessInventoryParams(queryParams);
        this.service.setView('adminMessInventory');
        return;
      }

      this.router.navigate(['/adminMessInventory'], { queryParams });
    }
  }

  private loadDivisionReport(): void {
    if (!this.month || !this.year || !this.divisionName) {
      return;
    }
    this.spinner.show();
    this.service.adminDivisionReport(this.month, this.year, this.divisionName).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? {};
        const reportMonth = String(data.month ?? '').trim();
        if (reportMonth) {
          this.syncSelectedMonthFromReport(reportMonth);
        }
        this.divisionName = String(data.divisionName ?? this.divisionName).trim();
        this.kitchensCount = Number(data.kitchensCount ?? 0);
        this.summary = this.mapSummary(data.summary ?? {});
        this.kitchens = this.mapKitchens(data.kitchens ?? []);
        this.refreshVisibleDays();
        this.buildColumnDefs();
        this.rowData = this.kitchens.map((kitchen) => this.mapKitchenToGridRow(kitchen));
        this.applyGridData();
        this.spinner.hide();
      },
      error: () => {
        this.summary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
        this.kitchensCount = 0;
        this.kitchens = [];
        this.visibleDays = [];
        this.rowData = [];
        this.monthlyColDefs = [];
        this.applyGridData();
        this.spinner.hide();
      },
    });
  }

  private applyGridData(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption('columnDefs', this.monthlyColDefs);
    this.gridApi.setGridOption('rowData', this.rowData);
    this.gridApi.setGridOption('defaultColDef', this.divisionDefaultColDef);
    this.gridApi.resetColumnState();
    this.syncLayoutHeights();
  }

  private syncLayoutHeights(): void {
    setTimeout(() => {
      const headerRow = document.querySelector(
        '.division-monthly-grid-wrap .ag-header-row',
      ) as HTMLElement | null;
      if (headerRow?.offsetHeight) {
        this.kitchenHeaderHeight = headerRow.offsetHeight;
      }

      const agRows = document.querySelectorAll('.division-monthly-grid-wrap .ag-row');
      this.kitchenRowHeights = Array.from(agRows).map((row) => (row as HTMLElement).offsetHeight);
    }, 50);
  }

  private buildColumnDefs(): void {
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    const dayCols: ColDef[] = [];

    for (const d of this.visibleDays) {
      const field = `day_${d}`;
      const dow = moment({ year, month: monthIndex, day: d }).format('ddd');
      dayCols.push({
        field,
        headerName: `${d}\n${dow}`,
        flex: 1,
        minWidth: this.monthlyDayMinWidth,
        sortable: false,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerClass: 'monthly-day-header',
        cellClass: 'monthly-day-cell',
        cellStyle: this.monthlyDayCellStyle,
        cellRenderer: this.monthlyDayStatusCellRenderer,
        valueFormatter: () => '',
      });
    }

    this.monthlyColDefs = dayCols;
  }

  private mapKitchenToGridRow(kitchen: KitchenRowView): Record<string, unknown> {
    const row: Record<string, unknown> = {
      messBuildingId: kitchen.messBuildingId,
      messBuildingName: kitchen.messBuildingName,
      entityName: kitchen.entityName,
    };

    for (const d of this.visibleDays) {
      const field = `day_${d}`;
      const entry = kitchen.dayStatusMap[d];
      if (entry) {
        const statusKey = this.resolveDayStatusKey(entry);
        row[field] = statusKey;
        row[`${field}_meta`] = entry;
      } else {
        row[field] = '';
      }
    }

    return row;
  }

  private monthlyDayStatusCellRenderer = (params: ICellRendererParams): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'monthly-day-dot-wrap';
    const dot = document.createElement('span');
    dot.className = `monthly-day-dot monthly-day-dot--${this.statusToDotModifier(String(params.value ?? ''))}`;
    dot.setAttribute('role', 'presentation');
    wrap.appendChild(dot);
    return wrap;
  };

  private monthlyDayCellStyle = (): Record<string, string> => ({
    padding: '5px 3px',
    border: '1px solid #eef2f7',
    background: '#fafbfc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  private mapSummary(raw: any): MonthlySummary {
    return {
      closed: Number(raw?.closed ?? 0),
      pending: Number(raw?.pending ?? 0),
      holiday: Number(raw?.holiday ?? 0),
      notSubmitted: Number(raw?.notSubmitted ?? 0),
    };
  }

  private mapKitchens(rawKitchens: any[]): KitchenRowView[] {
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    return rawKitchens.map((kitchen) => {
      const dailyStatus: DailyStatusEntry[] = (Array.isArray(kitchen?.dailyStatus) ? kitchen.dailyStatus : []).map(
        (entry: any) => ({
          messBuildingId: Number(entry?.messBuildingId ?? kitchen?.messBuildingId ?? 0),
          messBuildingName: String(entry?.messBuildingName ?? kitchen?.messBuildingName ?? '').trim(),
          daysheetCloseId: entry?.daysheetCloseId ?? null,
          daysheetDate: String(entry?.daysheetDate ?? '').trim(),
          status: String(entry?.status ?? 'NOT_SUBMITTED').trim(),
          holiday: !!entry?.holiday,
          holidayReason: String(entry?.holidayReason ?? '').trim(),
          remarks: entry?.remarks ?? null,
        }),
      );
      const dayStatusMap: Record<number, DailyStatusEntry | null> = {};
      for (let d = 1; d <= this.daysInMonth; d++) {
        dayStatusMap[d] = null;
      }
      for (const entry of dailyStatus) {
        const parsed = moment(entry.daysheetDate, ['DD MMM YYYY', 'D MMM YYYY'], true);
        if (parsed.isValid() && parsed.year() === year && parsed.month() === monthIndex) {
          dayStatusMap[parsed.date()] = entry;
        }
      }
      return {
        messBuildingId: Number(kitchen?.messBuildingId ?? 0),
        messBuildingName: String(kitchen?.messBuildingName ?? '').trim(),
        entityName: String(kitchen?.entityName ?? kitchen?.messBuildingName ?? '').trim(),
        summary: this.mapSummary(kitchen?.summary ?? {}),
        dailyStatus,
        dayStatusMap,
      };
    });
  }

  private refreshVisibleDays(): void {
    this.daysInMonth = this.selectedMonth.daysInMonth();
    this.visibleDays = this.getMonthlyVisibleDays();
    this.monthLabel = this.selectedMonth.format('MMM YYYY');
  }

  private getMonthlyVisibleDays(): number[] {
    const daysInMonth = this.selectedMonth.daysInMonth();
    const today = moment();
    const isCurrentMonth = this.selectedMonth.isSame(today, 'month');
    const lastDay = isCurrentMonth ? Math.min(today.date(), daysInMonth) : daysInMonth;
    const days: number[] = [];
    for (let d = lastDay; d >= 1; d--) {
      days.push(d);
    }
    return days;
  }

  private syncSelectedMonth(): void {
    const monthNum = Number(this.month);
    const yearNum = Number(this.year);
    if (!monthNum || !yearNum) {
      return;
    }
    this.selectedMonth = moment({ year: yearNum, month: monthNum - 1, day: 1 }).startOf('month');
    this.daysInMonth = this.selectedMonth.daysInMonth();
    this.monthLabel = this.selectedMonth.format('MMM YYYY');
  }

  private syncSelectedMonthFromReport(monthText: string): void {
    const yearNum = Number(this.year) || this.selectedMonth.year();
    const withYear = monthText.match(/\d{4}/) ? monthText : `${monthText} ${yearNum}`;
    const parsed = moment(withYear, ['MMM YYYY', 'MMMM YYYY', 'MM YYYY'], true);
    if (parsed.isValid()) {
      this.selectedMonth = parsed.clone().startOf('month');
      this.daysInMonth = this.selectedMonth.daysInMonth();
      this.monthLabel = this.selectedMonth.format('MMM YYYY');
    }
  }

  private resolveDayStatusKey(entry: DailyStatusEntry): string {
    if (entry.holiday) {
      return 'HOLIDAY';
    }
    const status = String(entry.status ?? 'NOT_SUBMITTED').trim();
    return status || 'NOT_SUBMITTED';
  }

  private statusToDotModifier(statusKey: string): string {
    switch (statusKey) {
      case 'CLOSED':
        return 'closed';
      case 'PENDING':
        return 'pending';
      case 'HOLIDAY':
        return 'holiday';
      case 'NOT_SUBMITTED':
        return 'not-submitted';
      default:
        return 'empty';
    }
  }
}
